import type { ReadonlyRecord } from '../../../../utils'
import type { SubscriptionsTypes } from '../../../../application'

import type { BackendPath } from '../../../Connectware'
import type { HttpRequestArgs, HttpRequestBody } from '../../Base'
import type { SubscriptionStrategy } from './Strategy'
import {
    AgentsStrategy,
    ConnectionsStrategy,
    CoreContainerStrategy,
    DetailedConnectionStrategy,
    DetailedCoreContainerStrategy,
    DetailedEndpointStrategy,
    DetailedMappingStrategy,
    DetailedServiceContainerStrategy,
    DetailedServiceStrategy,
    DetailedVolumeStrategy,
    DeviationsStrategy,
    EndpointsStatusStrategy,
    EndpointsStrategy,
    MappingsStatusStrategy,
    MappingsStrategy,
    NodesStatusStrategy,
    NodesStrategy,
    ServerStrategy,
    ServiceContainersStrategy,
    ServicesDataResourcesStrategy,
    ServicesLinksStrategy,
    ServicesStrategy,
    VolumesStrategy,
} from './implementations'

type StrategyConstructor<T extends keyof SubscriptionsTypes> = new (
    request: <Path extends BackendPath, ReqBody extends HttpRequestBody, Return>(args: HttpRequestArgs<Path, ReqBody, Return>) => Promise<Return>
) => SubscriptionStrategy<T>

const strategyCreators: ReadonlyRecord<keyof SubscriptionsTypes, StrategyConstructor<keyof SubscriptionsTypes>> = {
    services: ServicesStrategy,
    service: DetailedServiceStrategy,
    servicesLinks: ServicesLinksStrategy,
    serviceResources: ServicesDataResourcesStrategy,
    mappingStatus: MappingsStatusStrategy,
    nodeStatus: NodesStatusStrategy,
    endpointStatus: EndpointsStatusStrategy,
    endpoints: EndpointsStrategy,
    endpoint: DetailedEndpointStrategy,
    serviceDeviations: DeviationsStrategy,
    volumes: VolumesStrategy,
    volume: DetailedVolumeStrategy,
    serviceContainers: ServiceContainersStrategy,
    serviceContainer: DetailedServiceContainerStrategy,
    coreContainers: CoreContainerStrategy,
    coreContainer: DetailedCoreContainerStrategy,
    connections: ConnectionsStrategy,
    connection: DetailedConnectionStrategy,
    mappings: MappingsStrategy,
    mapping: DetailedMappingStrategy,
    agents: AgentsStrategy,
    nodes: NodesStrategy,
    servers: ServerStrategy,
    server: ServerStrategy,
}

export const getSubscriptionStrategyFactory = <T extends keyof SubscriptionsTypes>(type: T): StrategyConstructor<T> =>
    strategyCreators[type] as (typeof strategyCreators)[typeof type] & StrategyConstructor<T>
